// eslint-disable-next-line jsdoc/require-jsdoc
import { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import usePlan from '../hooks/usePlan';

/**
 * Global banner shown when a subscription is expiring soon or in grace period.
 * @returns Banner element or null.
 */
export default function PlanExpiryBanner(): ReactElement | null {
  const { planStatus, planName, daysUntilExpiry, graceDaysRemaining } = usePlan();
  const navigate = useNavigate();

  if (planStatus === 'expiring-soon' && daysUntilExpiry !== null) {
    return (
      <div className="flex items-center justify-between gap-4 bg-warning-500/10 border-b border-warning-500/30 px-6 py-2.5 text-sm text-warning-300">
        <div className="flex items-center gap-2">
          <span className="material-icons-outlined text-base leading-none">warning</span>
          Your <span className="font-semibold">{planName}</span> plan expires in{' '}
          <span className="font-semibold">{daysUntilExpiry} day{daysUntilExpiry !== 1 ? 's' : ''}</span>.
          After expiry you have a 10-day grace period before being moved to the Free plan.
        </div>
        <button
          type="button"
          onClick={() => navigate('/pricing')}
          className="shrink-0 rounded border border-warning-400 px-3 py-1 text-xs font-semibold text-warning-300 hover:bg-warning-500/20"
        >
          Renew Now
        </button>
      </div>
    );
  }

  if (planStatus === 'grace-period' && graceDaysRemaining !== null) {
    return (
      <div className="flex items-center justify-between gap-4 bg-error-500/10 border-b border-error-500/30 px-6 py-2.5 text-sm text-error-300">
        <div className="flex items-center gap-2">
          <span className="material-icons-outlined text-base leading-none">error</span>
          Your <span className="font-semibold">{planName}</span> plan has expired.{' '}
          {graceDaysRemaining > 0 ? (
            <>
              You will be moved to the <span className="font-semibold">Free plan</span> in{' '}
              <span className="font-semibold">{graceDaysRemaining} day{graceDaysRemaining !== 1 ? 's' : ''}</span>{' '}
              if you don&apos;t upgrade.
            </>
          ) : (
            <>You are being moved to the Free plan.</>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate('/pricing')}
          className="shrink-0 rounded border border-error-400 px-3 py-1 text-xs font-semibold text-error-300 hover:bg-error-500/20"
        >
          Upgrade Now
        </button>
      </div>
    );
  }

  return null;
}
